import React, { useEffect, useState } from 'react';
import { Button, Flex, Popconfirm, Typography, Select, Space, Badge, Tooltip, message } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { EditOutlined, DeleteOutlined, FileTextOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import dayjs from 'dayjs';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';


// Search, filters and the page live in the URL so the view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 100 },
  stage: { type: 'string' },
  owner: { type: 'string' },
};

const DealsTable = ({ hideHeader = false }) => {

  const navigate = useNavigate();

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Backend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [, setSelectedRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const filterStage = filters.stage || '';
  const setFilterStage = urlSetter('stage');
  const filterOwner = filters.owner || '';
  const setFilterOwner = urlSetter('owner');

  const fetchDeals = async (page, size) => {
    setLoading(true);
    try {
      const response = await actions.loadDeals(page, searchTerm, filterStage, filterOwner, size);
      if (response.status === 200) {
        const data = response.data;
        setDataSource(data.results || []);
        setTotalRows(data.count || 0);
      }
    } catch (e) {
      console.error('Error fetching deals:', e);
    }
    setLoading(false);
  }

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page on search
    }
  }

  // Fetch deals when filters or pagination change
  useEffect(() => {
    fetchDeals(currentPage, pageSize);
  }, [searchTerm, filterStage, filterOwner, currentPage, pageSize]);

  const handleDelete = async (id) => {
    const response = await actions.deleteDeal(id);
    if (response) {
      // Refresh the data after deletion
      fetchDeals(currentPage, pageSize);
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRecords(selectedRows);
    },
  }

  const defaultColumns = [
    {
      title: 'Number',
      dataIndex: 'deal_number',
      key: 'deal_number',
      sorter: (a, b) => (a?.deal_number || '').localeCompare(b?.deal_number || ''),
      width: 130,
      fixed: 'left',
    },
    {
      title: 'Deal Name',
      dataIndex: 'deal_name',
      key: 'deal_name',
      width:220,
      sorter: (a, b) => a?.deal_name.localeCompare(b?.deal_name),
      render: (text, record) => (
        <a onClick={() => navigate(`/deals/edit/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: 'Account',
      dataIndex: 'account_name',
      key: 'account_name',
      width:220,
      sorter: (a, b) => (a?.account_name || '').localeCompare(b?.account_name || ''),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width:220,
      sorter: (a, b) => (a?.amount || 0) - (b?.amount || 0),
      render: (text) => `Rs. ${text || 0}`,
    },
    {
      title: 'Stage',
      dataIndex: 'stage_display',
      key: 'stage_display',
      width:220,
      sorter: (a, b) => (a?.stage_display || '').localeCompare(b?.stage_display || ''),
    },
    {
      title: 'Closing Date',
      dataIndex: 'closing_date',
      key: 'closing_date',
      width:220,
      sorter: (a, b) => dayjs(a?.closing_date) - dayjs(b?.closing_date),
      render: (text) => text ? dayjs(text).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Owner',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width:220,
      sorter: (a, b) => (a?.owner_name || '').localeCompare(b?.owner_name || ''),
    },
    {
      title: 'Probability',
      dataIndex: 'probability',
      key: 'probability',
      width:220,
      render: (text) => `${text || 0}%`,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Copy Number">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                const textToCopy = record.deal_number || '';
                const textArea = document.createElement('textarea');
                textArea.value = textToCopy;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                message.success('Deal number copied!');
              }}
            >
            </Button>
          </Tooltip>
          <Tooltip title="Edit Deal">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/deals/edit/${record.id}`)}
            >
            </Button>
          </Tooltip>
          <Tooltip title="Create Quote">
            <Badge count={record.quotes_count || 0} showZero={false} size="small">
              <Button
                type="link"
                icon={<FileTextOutlined />}
                onClick={() => navigate(`/quotes/new?deal=${record.id}`)}
                style={{ color: '#1890ff' }}
              >
              </Button>
            </Badge>
          </Tooltip>
          <Popconfirm
            title="Delete Deal"
            description="Are you sure you want to delete this deal?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Handle pagination change
  const handleTableChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    } else {
      setCurrentPage(page);
    }
  };

  // Search is not part of the filter panel, so it is neither counted here nor
  // cleared by "Clear All".
  const activeFilterCount = (filterStage ? 1 : 0) + (filterOwner ? 1 : 0);

  const clearFilters = () => {
    setFilterStage('');
    setFilterOwner('');
    setCurrentPage(1);
  };

  const handleFilterChange = (type, value) => {
    if (type === 'stage') {
      setFilterStage(value || '');
    } else if (type === 'owner') {
      setFilterOwner(value || '');
    }
    setCurrentPage(1); // Reset to first page on filter change
  };

  return (
    <div style={{ padding: hideHeader ? '0' : '20px' }}>
      {!hideHeader && (
        <Flex justify="space-between" align="center" wrap="wrap" style={{ marginBottom: 16 }}>
          <Typography.Title level={3}>All Deals</Typography.Title>
          <Flex gap="middle">
            <Select
              placeholder="Actions"
              style={{ width: 120 }}
              allowClear
            >
              <Select.Option value="delete">Delete</Select.Option>
              <Select.Option value="export">Export</Select.Option>
            </Select>
          </Flex>
        </Flex>
      )}

      <div style={{ marginBottom: 8 }}>
        Showing {totalRows === 0 ? 0 : ((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} deals
      </div>
      <FilterBar
        searchValue={searchTermLive}
        onSearchChange={setSearchTermLive}
        onSearch={onSearch}
        searchPlaceholder="Search deals"
        activeCount={activeFilterCount}
        onClear={clearFilters}
        title="Filter Deals"
        addLabel="Create Deal"
        onAdd={() => navigate('/deals/new')}
        onRefresh={() => fetchDeals(currentPage, pageSize)}
        refreshing={loading}
      >
        <FilterField label="Stage">
          <Select
            placeholder="Filter by Stage"
            allowClear
            value={filterStage || undefined}
            onChange={(value) => handleFilterChange('stage', value)}
          >
            <Select.Option value="QUALIFICATION">Qualification</Select.Option>
            <Select.Option value="NEEDS_ANALYSIS">Needs Analysis</Select.Option>
            <Select.Option value="VALUE_PROPOSITION">Value Proposition</Select.Option>
            <Select.Option value="PROPOSAL_PRICE_QUOTE">Proposal/Price Quote</Select.Option>
            <Select.Option value="CLOSED_WON">Closed Won</Select.Option>
            <Select.Option value="CLOSED_LOST">Closed Lost</Select.Option>
          </Select>
        </FilterField>
      </FilterBar>

      <Table
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        columns={defaultColumns}
        dataSource={dataSource}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalRows,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['50', '100', '200'],
          onChange: handleTableChange,
        }}
        rowKey="id"
        size="small"
        scroll={{
            x: 'max-content',        // Enables horizontal scroll
            y: 'calc(100vh - 350px)' // Vertical scroll
            }}
             sticky
      />
    </div>
  );
};

export default DealsTable;
